import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, Directive, ElementRef, Host, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { PlotlyComponent, PlotlyService } from 'angular-plotly.js';
import { Layout, LayoutAxis } from 'plotly.js';
import { Observable, Subject, Subscription, zip } from 'rxjs';
import { debounceTime, filter, first, repeat } from 'rxjs/operators';
import { LogService } from 'src/app/services/loggers/logger.service';
import { getAxesFromSubplotKey, getAxisLongFromShortName, getAxisShortFromLongName, isAxisEntry, isAxisPath } from 'src/app/utility/plotly';
import { SbiResizeObserver } from 'src/app/utility/resize-observer';
import { createSvgElement } from 'src/app/utility/svg';
import { SubSink } from 'subsink';


/**
 * A directive that when having it's onRelayout-function triggered injects
 * reset-zoom buttons into the plotly plot if axes are zoomed.
 *
 * Will remove the buttons once the axes are reset.
 *
 * The buttons are injected into the plotly plot's drag layer.
 *
 * @remarks
 * As there are currently (13.05.25) only at max three rows
 * and just one column in our plots, using this directive on
 * plots with more lines or columns will result in
 * undefined/likely-faulty behavior.
 * @remarks Only methods marked with `@throws` can throw an error.
 * @remarks If the directive encounters an error, it will remove buttons
 *  and stop working. This is to prevent dead objects or faulty behavior.
 *  Errors should be logged.
 */
@Directive({
  selector: 'plotly-plot[appResetZoomButtons]',
  standalone: true
})
export class ResetZoomButtonsDirective implements OnInit, AfterViewInit, OnDestroy {

  /**
   * Allows to conditionally-enable/disable the directive.
   */
  @Input({ required: false })
  public set appResetZoomButtons(val: BooleanInput) {
    this.isHealthy = coerceBooleanProperty(val);
  }


  /**
   * Observable which' emissions will trigger the
   * removal of all "Reset zoom"-buttons.
   */
  @Input({ required: false })
  public set appResetZoomButtonsReset(trigger: Observable<void>) {
    this.resetZoomButtonsSubscription?.unsubscribe();
    this.resetZoomButtonsSubscription = trigger.subscribe(
      () => this.clearButtons()
    );
  }

  /**
   * A list of axes that are zoomed in.
   *
   * This is used to determine if the reset buttons should be shown or not.
   */
  @Input({ required: true })
  public appResetZoomButtonsZoomedAxes: Set<string> = new Set<string>();


  /**
   * Backing field of {@link appResetZoomButtonsReset}
   * which holds the subscription to the observable which
   * triggers the removal of all "Reset zoom"-buttons.
   */
  private resetZoomButtonsSubscription?: Subscription;


  /**
   * The html element of the hosting <plotly-plot />
   */
  private element: HTMLElement;


  /**
   * Manages rxjs subscriptions
   */
  private subsink = new SubSink();


  /**
   * The buttons that have been injected into the plotly plot.
   */
  private buttons: { [row: string]: null | SVGElement } = {
    0: null,
    1: null,
    2: null
  };


  /**
   * Backing field of {@link isHealthy}
   *
   * Do NOT use directly!
   */
  private _isHealthy = true;


  /**
   * Indicates if the plotly plot is healthy and the directive
   * can work.
   */
  private get isHealthy(): boolean {
    return this._isHealthy;
  }


  private set isHealthy(value: boolean) {
    this._isHealthy = value;

    if (!value) {
      this.clearButtons();
    }
  }


  /**
   * The Plotly-instance
   */
  private plotly: any;


  /**
   * The layer which will contain the buttons
   *
   * @remarks Lazy-initialized the first time it is needed.
   * @see {@link getButtonLayer}
   */
  private buttonLayer?: SVGElement;


  /**
   * @throws On error (e.g. if the plotly plot is not healthy or construction fails)
   */
  constructor(
    element: ElementRef,
    private log: LogService,

    /**
     * The Plotly component this directive is used on.
     *
     * Optional, because this directive could in theory be applied to
     * non-`plotly-plot`-elements.
     */
    @Optional()
    @Host()
    private plotComponent: PlotlyComponent | null,

    private plotlyService: PlotlyService
  ) {
    this.element = element.nativeElement;
    if (!this.plotComponent) {
      throw new Error('AutoResetZoomButtonsDirective must be used on a PlotlyComponent');
    }

    this._onResetButtonPress = this._onResetButtonPress.bind(this);
  }


  /**
   * @private
   */
  public async ngOnInit() {
    try {
      this.plotly = await this.plotlyService.getPlotly();
    } catch (ex) {
      this.log.error('Failed to get plotly instance', ex);
      this.isHealthy = false;
    }
  }


  /**
   * @private
   */
  public ngAfterViewInit(): void {
    try {
      // Should be a div/or other measurable element (e.g. not inline)
      const target = this.element.firstElementChild!;

      const resize$ = new Subject<void>();
      const afterPlot$ = new Subject<void>();

      this.subsink.sink = SbiResizeObserver
        .observe(target)
        .subscribe(() => resize$.next());


      this.subsink.sink = this.plotComponent!.afterPlot
        .subscribe(afterPlot$);

      this.subsink.sink = zip(resize$, afterPlot$)
        .pipe(
          debounceTime(40),
          filter(() => this.isHealthy),
          first(),
          repeat(),
        )
        .subscribe(() => this.repositionButtons());
    } catch (e) {
      // No need to let this error propagate, as the reset-buttons feature
      // is not critical.
      this.log.error(
        'Failed to set up the resize listener for the reset buttons',
        e
      );
      this.isHealthy = false;
    }
  }


  /**
   * @private
   */
  public ngOnDestroy(): void {
    try {
      this.subsink.unsubscribe();

      if (this.resetZoomButtonsSubscription !== undefined) {
        this.resetZoomButtonsSubscription.unsubscribe();
      }

      this.clearButtons();
    } catch (e) {
      this.log.error('Failed to clean up reset button directive', e);
    }
  }


  /**
   * Handler for the `relayout` event of the plotly component.
   *
   * This is where the "magic" happens.
   *
   * Checks the zoom state of the axes and injects reset buttons
   * if necessary and removes zoom buttons if they are not needed
   * anymore.
   *
   * @param layout The layout-changeset of the plotly element, as plotly
   *  would emit it on a relayout event.
   */
  public onRelayout(layout: Partial<Layout>) {
    if (!this.isHealthy) {
      return;
    }

    try {
      const changeSet  = this.extractAxesZoomStateFromLayoutUpdate(layout);
      const fullLayout = this.getFullLayout();

      /**
       * Map each row to its zoom state.
       *
       * Zooms overwrite resets, so if a row has both a zoom and a reset,
       * the zoom wins.
       */
      const rowZoomStates = this.determineRowZoomStatesFromChanges(
        changeSet,
        fullLayout
      );

      const svg = this.element.querySelector('svg');
      if (!svg) {
        throw Error('<svg>-element not found');
      }

      for (const [rowIdx, zoomed] of rowZoomStates) {
        const rowKey      = this.getSubplotKeyFromRowIdx(rowIdx, fullLayout);
        const rowElement  = this.element.querySelector(`.draglayer > .${rowKey}`);
        const resetButton = this.buttons[rowIdx];

        if (zoomed) {
          if (rowElement && !resetButton) {
            this.injectResetButtonIntoRow(
              rowIdx
            );
          }
        } else {
          if (resetButton) {
            resetButton.remove();
            this.buttons[rowIdx] = null;
          }
        }
      }
    } catch (e) {
      this.log.error('Failed to process relayout event', e);
      this.isHealthy = false;
    }
  }


  /**
   * From the given changeset and layout determin if the rows in the layout
   * are in a zoomed-in state or not.
   *
   * @param changeSet The chages
   * @param layout The layout
   * @returns Map object, mapping the row indices (top=0, center=1, bottom=2)
   *   to a boolean value indicating if the row was zoomed in (`true`) or
   *  the row's axes were reset (`false`)
   */
  private determineRowZoomStatesFromChanges(
    changeSet: Map<string, boolean>,
    layout: Partial<Layout>
  ): Map<number, boolean> {
    const rowsWithZoomState: Map<number, boolean> = new Map();

    for (const change of changeSet) {
      const row = this.determineRowIndexOfAxisInLayout(change[0], layout);
      const zoomed = change[1];
      if (!rowsWithZoomState.has(row)) {
        rowsWithZoomState.set(row, change[1]);
      }

      const state = rowsWithZoomState.get(row)!;
      if ((state === false || zoomed === true) && state !== zoomed) {
        rowsWithZoomState.set(row, zoomed);
      }
    }

    return rowsWithZoomState;
  }


  /**
   * Returns the row of the given axis in the given layout.
   *
   * Note: currently does not support overlaying x-axes!
   *
   * @param axis The axis name
   * @param layout The layout
   * @returns The row of the axis in the layout (top=0, center=1, bottom=2)
   */
  private determineRowIndexOfAxisInLayout(axisName: string, layout: Partial<Layout>): number {
    let targetName = axisName;
    const axis = layout[axisName as keyof Layout] as Partial<LayoutAxis>;
    if (axis.overlaying) {
      // Continue with the overlayed axis, as this one is mapped to the
      // x-axis we are looking for inside `layout.grid.subplots` later on

      targetName = getAxisLongFromShortName(axis.overlaying);
    }

    // Find the subplot the axis is in
    return this.determineSubplotIndexOfAxis(targetName, layout);
  }


  /**
   * Determine the index of the subplot the given axis is in.
   *
   * @param axisName The long name of the axis. Providing the shorthand name
   *   results in invalid behaviour.
   * @param layout The layout containing the subplots
   * @returns Index of subplot
   */
  private determineSubplotIndexOfAxis(axisName: string, layout: Partial<Layout>): number {
    const short = getAxisShortFromLongName(axisName);
    return (layout
      .grid!
      .subplots! as unknown as string[][])
      .findIndex(subplot => subplot.some(pair => this.includesAxis(pair, short)));
  }


  /**
   * Check if `source` includes `axis`
   *
   * Circumvents the issue arising when looking for `x` in e.g. `x2` using
   * `includes` or `indexOf`.
   *
   * @param source The source string
   * @param axis The axis to look for
   * @returns `true` if `source` includes `axis`, `false` otherwise
   */
  private includesAxis(source: string, axis: string): boolean {
    const idx = source.indexOf(axis);
    if (idx === -1) {
      return false;
    }

    const next = source[idx + axis.length];
    const notFollowedByNumber = Number.isNaN(Number(next));

    if (notFollowedByNumber) {
      return true;
    }

    return false;
  }


  /**
   * Returns all axes and their zoom states
   *
   * @param layout The layout update
   * @returns Map object, mapping the axis keys/names (e.g. xaxis3)
   *   to a boolean value indicating if the axis was zoomed in (`true`)
   *   or the axis zoom reset (`false`)
   */
  private extractAxesZoomStateFromLayoutUpdate(
    layout: Partial<Layout>
  ): Map<string, boolean> {
    const result = new Map<string, boolean>();

    for (const key of Object.keys(layout)) {
      if (isAxisPath(key)) {
        const axis = this.extractAxisFromPath(key) as keyof Layout;

        if (this.appResetZoomButtonsZoomedAxes.has(axis)) {
          result.set(axis, true);
        } else {
          result.set(axis, false);
        }

      }
    }

    return new Map(result);
  }


  /**
   * Extract the axis key/name from the given axis path.
   *
   * E.g. `xaxis3.range[0]` -> `xaxis3`
   *
   * @param path The axis path
   * @returns The axis key/name
   */
  private extractAxisFromPath(path: string): string {
    const parts = path.split('.');
    return parts[0];
  }


  /**
   * Inject new reset button into the designated presentation
   * layer of the plotly plot at y-position `y`
   *
   * Once pressed, the button will reset the axes of the row
   *
   * @param rowIdx The index of the row in the grid. Used to determine which
   *     axes to reset.
   * @param y Y-position of the button
   * @throws If a reset button for the given row already exists
   */
  private injectResetButtonIntoRow(rowIdx: number): void {
    if (this.buttons[rowIdx]) {
      throw new Error(`Reset-button for row ${rowIdx} already exists`);
    }

    // Create the button and set its y-position according to
    // the row's y-position in the plot

    const y      = this.getYPositionOfRow(rowIdx);
    const button = this.createResetButton(rowIdx);
    button.setAttribute('y', `${Math.max(y, 0)}`);

    // Store a reference to the button for later use
    this.buttons[rowIdx] = button;

    this.getButtonLayer().appendChild(button);
  }


  /**
   *
   * @param rowIdx The index of the row in the grid. Used to determine which
   *   axes to reset.
   * @returns The SVG element (`<foreignObject>`) representing the reset button.
   */
  private createResetButton(rowIdx: number): SVGElement {
    //// Create a <forgeinObject> to hold the button

    const fo = createSvgElement('foreignObject');
    fo.setAttribute('width', '100');
    fo.setAttribute('height', '40');
    fo.classList.add('reset-zoom-button', 'chart-zoom-reset-button');

    //// Create the button

    const button = document.createElement('button');
    button.textContent = 'Reset zoom';
    button.addEventListener('click', () => void this._onResetButtonPress(rowIdx));
    button.title = 'Reset zoom for this row';

    // Ensure the button is interactive
    button.classList.add('pointer-events-auto');

    button.style.backgroundColor = 'white';

    // Highlighting of associated rows on hover
    button.addEventListener('mouseover', () => this.highlightRow(rowIdx));
    button.addEventListener('mouseout', () => this.clearRowHighlighting(rowIdx));

    // Add data-attribute for e2e testing
    button.dataset.cy = 'reset_zoom_button_row_' + rowIdx;

    fo.appendChild(button);

    return fo;
  }


  /**
   * Handler for the click event of the reset button.
   *
   * @private
   * @param rowIdx The index of the row to reset axes for
   */
  private async _onResetButtonPress(rowIdx: number) {
    try {
      // Reset axes's ranges to autorange, so the chart view tab
      // will be triggered to update the sub-plots accordingly

      const axes       = this.findAxesByRow(rowIdx);
      const changeSet: { [key: string]: boolean } = {};

      for (const axis of axes) {
        changeSet[`${axis}.autorange`] = true;
      }


      // Remove possible row-highlights
      this.clearRowHighlighting(rowIdx);


      // Trigger a re-layout.
      // This shall notify any listeners of the plotly event,
      // that the layout has changed.
      // Afterwards - to remove the buttons - this directive's relayout-function
      // should be called.

      // Trying to provide updates only as in the documentation
      // leads to a nullptr-exception trying to access some '_inputDomain' on
      // 'undefined' in 'plotly.js'.
      const ele = this.plotComponent!.plotlyInstance!;
      this.plotly.relayout(ele, changeSet);
    } catch (e) {
      this.log.error('Failed to reset zoom', e);
      this.isHealthy = false;
    }
  }


  /**
   * Find all axes present in the given row
   *
   * @param rowIdx The index of the row
   */
  private findAxesByRow(rowIdx: number): string[] {
    const layout = this.getFullLayout();
    const axisPair = this.getSubplotKeyFromRowIdx(rowIdx, layout);
    const subplotAxisSet = getAxesFromSubplotKey(axisPair);

    // Find axes overlaying the axes from the subplot key
    const result: string[] = [...subplotAxisSet.map(axis => getAxisLongFromShortName(axis))];

    const layoutAxisKeys = Object.entries(layout).filter(isAxisEntry)
      .filter(([,value]) => value.fixedrange !== true)
      .map(([key,]) => key);
    for (const axis of subplotAxisSet) {
      const overlaying = layoutAxisKeys.filter(
        key => (layout[key as keyof Layout] as LayoutAxis).overlaying === axis
      );

      result.push(...overlaying);
    }

    return result;
  }


  /**
   * Returns the layout of the plot component to which this directive
   * is applied to.
   *
   * 'full' as opposed to the 'partial' layout that is emitted by the
   * `relayout` event. Don't confuse this with TS's `Partial<T>` type.
   *
   * @returns
   */
  private getFullLayout(): Partial<Layout> {
    return this.plotComponent!.layout!;
  }


  /**
   * Takes a row index and returns the subplot key (axis-pair, e.g. 'x3y2')
   * from the given layout.
   *
   * @param rowIdx The index of the row in our graph
   * @param layout The full layout object
   * @returns the subplot key
   */
  private getSubplotKeyFromRowIdx(rowIdx: number, layout: Partial<Layout>): string {
    return layout.grid!.subplots![rowIdx][0];
    // using [0] here, because we support only one column - for now
  }


  /**
   * Removes all reset buttons from the plotly plot.
   */
  private clearButtons() {
    for (const [key, button] of Object.entries(this.buttons)) {
      if (button) {
        button.remove();
        this.buttons[key] = null;
      }
    }
  }


  /**
   * Reposition buttons
   *
   * Should be called after the plot's size has changed.
   * Otherwise buttons might be positioned incorrectly.
   */
  private repositionButtons() {
    try {
      Object.entries(this.buttons).forEach(([row, button]) => {
        if (!button) {
          return;
        }

        const y = this.getYPositionOfRow(Number(row));

        button?.setAttribute('y', `${y}`);
      });
    } catch (e) {
      this.log.error('Failed to re-position the reset buttons', e);
      this.isHealthy = false;
    }
  }


  /**
   *
   * @param rowIndex Index of the row in the plot (top=0, center=1, bottom=2)
   * @param layout The full layout object
   * @returns The y-position of the given row in the plot in pixels
   */
  private getYPositionOfRow(rowIndex: number): number {
    const layer = this.getRowLayer(rowIndex) as SVGGraphicsElement;

    return layer.getBBox().y - 10;
  }


  /**
   * Returns the layer in which the reset buttons are placed
   */
  private getButtonLayer(): SVGElement {
    if (!this.buttonLayer) {
      const layer = this.element.querySelector('svg g.menulayer');
      if (!layer) {
        throw new Error('Menu layer does not exist in plot');
      }

      this.buttonLayer = layer as SVGElement;
    }

    return this.buttonLayer;
  }


  /**
   * Get the layer of the given row index
   *
   * @param rowIdx The index of the row in the plot (top=0, center=1, bottom=2)
   * @returns The layer
   * @throws If the layer does not exist
   */
  private getRowLayer(rowIdx: number): SVGElement {
    const axisPair = this.getSubplotKeyFromRowIdx(rowIdx, this.getFullLayout());

    const layer = this.element.querySelector(`.draglayer > .${axisPair}`);
    if (!layer) {
      throw new Error(`Row layer ${axisPair} does not exist in plot`);
    }

    return layer as SVGElement;
  }


  /**
   * Highligts the row specified by `rowIdx`
   *
   * @param rowIdx The index of the row (top=0, center=1, bottom=2)
   */
  private highlightRow(rowIdx: number) {
    const layer    = this.getRowLayer(rowIdx);
    const dragRect = layer.querySelector('rect.nsewdrag') as SVGGraphicsElement;
    const box      = dragRect.getBBox();

    const rect = createSvgElement('rect');
    rect.classList.add('chart-zoom-row-highlight');
    rect.style.height = `${box.height}px`;
    rect.style.width = `${box.width}px`;
    rect.setAttribute('x', `${box.x}`);
    rect.setAttribute('y', `${box.y}`);

    layer.insertAdjacentElement('afterbegin', rect);
  }


  /**
   * Removes highlighting from the row specified by `rowIdx`
   *
   * @param rowIdx The index of the row (top=0, center=1, bottom=2)
   */
  private clearRowHighlighting(rowIdx: number) {
    const layer = this.getRowLayer(rowIdx);
    layer.querySelector('.chart-zoom-row-highlight')?.remove();
  }
}
